import React, { useState, useMemo } from 'react';
import { getStrategyConfig } from '../config/strategyConfig';
import CashflowBreakdown from './CashflowBreakdown';
import { safeParseFinancialBreakdown } from '../schemas/analysisResult.zod';

/**
 * DetailedMetrics Component
 * Displays detailed structured data from chart_config and agent results
 * that isn't shown in the main metric cards
 */
function DetailedMetrics({ strategyType, strategyData, result }) {
  const [activeTab, setActiveTab] = useState('financials');
  const config = getStrategyConfig(strategyType);
  
  if (!strategyData && !result) return null;

  const chartConfig = strategyData?.chart_config;
  const research = result?.research;
  const valuation = result?.valuation;
  const compliance = result?.compliance;

  const hasParsedBreakdown = useMemo(() => {
    const bd = strategyData?.financial_breakdown;
    if (!bd) return false;
    return safeParseFinancialBreakdown(bd).success;
  }, [strategyData?.financial_breakdown]);

  // Helper to format currency
  const formatCurrency = (val) => {
    if (val == null || val === undefined) return '—';
    return `£${Number(val).toLocaleString()}`;
  };

  // Helper to format percentage
  const formatPercent = (val) => {
    if (val == null || val === undefined) return '—';
    return `${Number(val).toFixed(1)}%`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className={`${config.bgColor} ${config.borderColor} border-b px-4 py-3`}>
        <h4 className={`font-bold ${config.textColor}`}>
          Detailed Analysis Data
        </h4>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 overflow-x-auto">
        {[
          { id: 'financials', label: 'Financials', icon: '💰' },
          { id: 'research', label: 'Research', icon: '📍' },
          { id: 'valuation', label: 'Valuation', icon: '📊' },
          { id: 'compliance', label: 'Compliance', icon: '✓' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? `${config.bgColor} ${config.textColor} border-b-2 border-current`
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <span className="mr-1">{tab.icon}</span>
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
          </button>
        ))}
      </div>

      <div className="p-3 sm:p-4 overflow-x-auto">
        {/* Financial Details Tab */}
        {activeTab === 'financials' && (
          <div className="space-y-4">
            {hasParsedBreakdown && (
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Strategy cashflow breakdown</h5>
                <CashflowBreakdown breakdown={strategyData.financial_breakdown} compact />
              </div>
            )}
            {/* Chart Config - Rent Estimate Details */}
            {!hasParsedBreakdown && chartConfig?.rent_estimate && (
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Rent Estimate Details</h5>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">Monthly Rent:</span>
                    <div className="font-semibold">{formatCurrency(chartConfig.rent_estimate.monthly_rent)}</div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">Confidence:</span>
                    <div className="font-semibold">
                      {(() => {
                        const c = chartConfig.rent_estimate.confidence;
                        if (c == null || !Number.isFinite(Number(c))) return '—';
                        const n = Number(c);
                        const pct = n > 1 ? n : n * 100;
                        return `${pct.toFixed(0)}%`;
                      })()}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">Method:</span>
                    <div className="font-semibold capitalize">
                      {chartConfig.rent_estimate.method?.replace(/_/g, ' ') || '—'}
                    </div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">Comparables Used:</span>
                    <div className="font-semibold">
                      {chartConfig.rent_estimate.comparables_used || '—'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Chart Config - Total Return Breakdown */}
            {(chartConfig?.income_return_annual != null || chartConfig?.capital_appreciation_annual != null) && (
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Total Return Breakdown</h5>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="bg-blue-50 p-2 rounded text-center">
                    <div className="text-xs text-gray-500">Income Return</div>
                    <div className="font-bold text-blue-700">{formatPercent(chartConfig.income_return_annual)}</div>
                  </div>
                  <div className="bg-green-50 p-2 rounded text-center">
                    <div className="text-xs text-gray-500">Capital Growth</div>
                    <div className="font-bold text-green-700">{formatPercent(chartConfig.capital_appreciation_annual)}</div>
                  </div>
                  <div className="bg-purple-50 p-2 rounded text-center">
                    <div className="text-xs text-gray-500">Total Return</div>
                    <div className="font-bold text-purple-700">{formatPercent(chartConfig.total_return_annual)}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Chart Config - Flip/Renovation Details */}
            {!hasParsedBreakdown && (chartConfig?.renovation_cost != null || chartConfig?.arv != null) && (
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Project Cost Breakdown</h5>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">Renovation Cost:</span>
                    <div className="font-semibold">{formatCurrency(chartConfig.renovation_cost)}</div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">After Repair Value:</span>
                    <div className="font-semibold">{formatCurrency(chartConfig.arv)}</div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">Holding Period:</span>
                    <div className="font-semibold">{chartConfig.holding_months || '—'} months</div>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">Selling Costs:</span>
                    <div className="font-semibold">
                      {chartConfig.selling_costs_percent != null 
                        ? `${(chartConfig.selling_costs_percent * 100).toFixed(1)}%`
                        : formatCurrency(chartConfig.selling_costs_amount)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Annual Growth Rate */}
            {chartConfig?.annual_growth_rate != null && (
              <div className="bg-yellow-50 p-3 rounded">
                <h5 className="text-sm font-semibold text-gray-700">Assumed Annual Growth Rate</h5>
                <div className="text-lg font-bold text-yellow-700">
                  {formatPercent(chartConfig.annual_growth_rate * 100)}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Used for 5-year projections. Based on location tier and current market conditions.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Research Tab */}
        {activeTab === 'research' && research && (
          <div className="space-y-4">
            {/* Location Quality Ratings */}
            <div>
              <h5 className="text-sm font-semibold text-gray-700 mb-2">Location Quality</h5>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-gray-50 p-2 rounded text-center">
                  <div className="text-xs text-gray-500">School Quality</div>
                  <div className={`font-bold capitalize ${
                    research.school_quality === 'excellent' ? 'text-green-600' :
                    research.school_quality === 'good' ? 'text-blue-600' :
                    research.school_quality === 'requires_improvement' ? 'text-orange-600' : 'text-gray-600'
                  }`}>
                    {research.school_quality || '—'}
                  </div>
                </div>
                <div className="bg-gray-50 p-2 rounded text-center">
                  <div className="text-xs text-gray-500">Family Friendly</div>
                  <div className={`font-bold ${research.family_friendly ? 'text-green-600' : 'text-gray-600'}`}>
                    {research.family_friendly != null ? (research.family_friendly ? 'Yes' : 'No') : '—'}
                  </div>
                </div>
                <div className="bg-gray-50 p-2 rounded text-center">
                  <div className="text-xs text-gray-500">Amenities</div>
                  <div className={`font-bold capitalize ${
                    research.amenities_rating === 'excellent' ? 'text-green-600' :
                    research.amenities_rating === 'good' ? 'text-blue-600' :
                    research.amenities_rating === 'moderate' ? 'text-yellow-600' :
                    research.amenities_rating === 'poor' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {research.amenities_rating || '—'}
                  </div>
                </div>
              </div>
            </div>

            {/* Crime Rate */}
            {research.crime_rate && (
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Crime Assessment</h5>
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold capitalize ${
                  research.crime_rate === 'low' ? 'bg-green-100 text-green-800' :
                  research.crime_rate === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {research.crime_rate} Risk
                </div>
              </div>
            )}

            {/* Data Quality Score */}
            {(() => {
              const dq = Number(research.research_data_quality);
              if (!Number.isFinite(dq) || dq <= 0) return null;
              return (
                <div className="bg-blue-50 p-3 rounded">
                  <h5 className="text-sm font-semibold text-gray-700">Research Data Quality</h5>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${dq}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-blue-700">{dq.toFixed(0)}%</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Percentage of data sources successfully retrieved for this location.
                  </p>
                </div>
              );
            })()}

            {/* Planning Applications */}
            {research.planning_applications_found > 0 && (
              <div className="bg-gray-50 p-3 rounded">
                <h5 className="text-sm font-semibold text-gray-700">Planning Activity</h5>
                <div className="text-lg font-bold text-gray-900">
                  {research.planning_applications_found} applications found
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Recent planning applications in the area may indicate development activity.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Valuation Tab */}
        {activeTab === 'valuation' && valuation && (
          <div className="space-y-4">
            {/* Valuation Confidence */}
            <div className="bg-gray-50 p-3 rounded">
              <h5 className="text-sm font-semibold text-gray-700">Valuation Confidence</h5>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(valuation.confidence || 0) * 100}%` }}
                  />
                </div>
                <span className="text-sm font-bold text-green-700">
                  {valuation.confidence != null ? `${(valuation.confidence * 100).toFixed(0)}%` : '—'}
                </span>
              </div>
            </div>

            {/* Comparables Used */}
            {valuation.comparables_used > 0 && (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 p-2 rounded">
                  <span className="text-xs text-gray-500">Comparables Used:</span>
                  <div className="font-bold text-blue-700">{valuation.comparables_used}</div>
                </div>
                <div className="bg-blue-50 p-2 rounded">
                  <span className="text-xs text-gray-500">Market Avg £/sqft:</span>
                  <div className="font-bold text-blue-700">
                    {valuation.price_per_sqft_market_avg != null 
                      ? `£${valuation.price_per_sqft_market_avg.toFixed(0)}`
                      : '—'}
                  </div>
                </div>
              </div>
            )}

            {/* Value Range */}
            <div>
              <h5 className="text-sm font-semibold text-gray-700 mb-2">Valuation Range</h5>
              <div className="flex items-center gap-2 text-sm">
                <div className="bg-gray-100 px-3 py-2 rounded">
                  <span className="text-gray-500">Low:</span>
                  <div className="font-semibold">{formatCurrency(valuation.value_range_low)}</div>
                </div>
                <div className="flex-1 bg-blue-100 px-3 py-2 rounded text-center">
                  <span className="text-blue-600">Estimated:</span>
                  <div className="font-bold text-blue-800">{formatCurrency(valuation.estimated_value)}</div>
                </div>
                <div className="bg-gray-100 px-3 py-2 rounded">
                  <span className="text-gray-500">High:</span>
                  <div className="font-semibold">{formatCurrency(valuation.value_range_high)}</div>
                </div>
              </div>
            </div>

            {/* Adjustment Factors */}
            {valuation.adjustment_factors && Object.keys(valuation.adjustment_factors).length > 0 && (
              <div>
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Valuation Adjustments</h5>
                <div className="space-y-1">
                  {Object.entries(valuation.adjustment_factors).map(([factor, amount]) => (
                    <div key={factor} className="flex justify-between text-sm bg-gray-50 p-2 rounded">
                      <span className="capitalize">{factor.replace(/_/g, ' ')}:</span>
                      <span className={`font-semibold ${amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {amount >= 0 ? '+' : ''}{formatCurrency(amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && compliance && (
          <div className="space-y-4">
            {/* Compliance Checklist */}
            <div className="grid grid-cols-2 gap-3">
              <div className={`p-3 rounded text-center ${compliance.aml_check_passed ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="text-xs text-gray-500">AML Check</div>
                <div className={`font-bold ${compliance.aml_check_passed ? 'text-green-700' : 'text-red-700'}`}>
                  {compliance.aml_check_passed ? '✓ Passed' : '✗ Failed'}
                </div>
              </div>
              <div className={`p-3 rounded text-center ${compliance.fca_requirements_met ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="text-xs text-gray-500">FCA Requirements</div>
                <div className={`font-bold ${compliance.fca_requirements_met ? 'text-green-700' : 'text-red-700'}`}>
                  {compliance.fca_requirements_met ? '✓ Met' : '✗ Not Met'}
                </div>
              </div>
            </div>

            {/* EPC Info */}
            <div className="bg-gray-50 p-3 rounded">
              <h5 className="text-sm font-semibold text-gray-700">Energy Performance Certificate (EPC)</h5>
              <div className="flex items-center gap-4 mt-2">
                <div>
                  <span className="text-xs text-gray-500">Valid:</span>
                  <div className={`font-bold ${compliance.epc_valid ? 'text-green-600' : 'text-red-600'}`}>
                    {compliance.epc_valid ? 'Yes' : 'No'}
                  </div>
                </div>
                {compliance.epc_rating && (
                  <div>
                    <span className="text-xs text-gray-500">Rating:</span>
                    <div className={`inline-block px-2 py-1 rounded font-bold ${
                      ['A', 'B'].includes(compliance.epc_rating) ? 'bg-green-100 text-green-800' :
                      ['C', 'D'].includes(compliance.epc_rating) ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {compliance.epc_rating}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* HMO License */}
            <div className="bg-gray-50 p-3 rounded">
              <h5 className="text-sm font-semibold text-gray-700">HMO Licensing</h5>
              <div className="flex items-center gap-4 mt-2">
                <div>
                  <span className="text-xs text-gray-500">Required:</span>
                  <div className={`font-bold ${compliance.hmo_license_required ? 'text-orange-600' : 'text-green-600'}`}>
                    {compliance.hmo_license_required ? 'Yes' : 'No'}
                  </div>
                </div>
                {compliance.hmo_license_status && (
                  <div>
                    <span className="text-xs text-gray-500">Status:</span>
                    <div className="font-bold capitalize">{compliance.hmo_license_status}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Planning Restrictions */}
            {compliance.planning_restrictions && compliance.planning_restrictions.length > 0 && (
              <div className="bg-orange-50 p-3 rounded border border-orange-200">
                <h5 className="text-sm font-semibold text-orange-800">Planning Restrictions</h5>
                <ul className="mt-2 space-y-1">
                  {compliance.planning_restrictions.map((restriction, idx) => (
                    <li key={idx} className="text-sm text-orange-700 flex items-start gap-2">
                      <span>⚠</span>
                      <span>{restriction}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Compliance Flags */}
            {compliance.flags && compliance.flags.length > 0 && (
              <div className="bg-red-50 p-3 rounded border border-red-200">
                <h5 className="text-sm font-semibold text-red-800">Compliance Flags</h5>
                <ul className="mt-2 space-y-1">
                  {compliance.flags.map((flag, idx) => (
                    <li key={idx} className="text-sm text-red-700 flex items-start gap-2">
                      <span>🚩</span>
                      <span>{flag}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DetailedMetrics;
